
import { Component, Vue, Emit, Prop, Ref } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Carousel, Slide } from 'vue-carousel';

import { SkeletonLoader } from '@warrenbrasil/nebraska-web';
import UpdateAccountInbox from '../UpdateAccountInbox/UpdateAccountInbox.vue';
import CheckingAccountInbox from '../CheckingAccountInbox/CheckingAccountInbox.vue';
import PortfolioSuggestedCard from '../PortfolioSuggestedCard/PortfolioSuggestedCard.vue';
import PortfolioTransactionCard from '../PortfolioTransationCard/PortfolioTransactionCard.vue';
import SuggestedTransactionCard from '../SuggestedTransactionCard/SuggestedTransactionCard.vue';
import PortfolioSuggestedModal from '../PortfolioSuggestedModal/PortfolioSuggestedModal.vue';
import CardNews from '../CardNews/CardNews.vue';
import { PortabilityRequestsCard } from './components/PortabilityRequestsCard';

import { SuggestionModule } from '@/modules/home/store/suggestions';
import { SuggestionType } from '@/modules/investments/types/suggestion';
import { getNews, PortabilityRequestsData } from '@/modules/home/services/bff';
import { NewsAPI, IFeedItem } from '@/modules/home/services/news';
import * as trackRedeemEvents from './track-redeem-events';
import * as trackerInvestmentEvents from './track-investment-events';
import { GlobalSummaryInbox } from '@/modules/common/services/graphql/server/server.sdk';

const customerModule = namespace('CustomerModule');
const newsApi = new NewsAPI();

enum typeFrom {
  SuggestionInvestment = 'suggestion-investment',
  SuggestionRedeem = 'suggestion-redeem'
}

@Component({
  components: {
    Carousel,
    Slide,
    UpdateAccountInbox,
    CheckingAccountInbox,
    PortfolioSuggestedCard,
    PortfolioSuggestedModal,
    CardNews,
    SkeletonLoader,
    PortfolioTransactionCard,
    SuggestedTransactionCard,
    PortabilityRequestsCard
  }
})
export default class OverviewInbox extends Vue {
  @customerModule.Getter('getCustomerId')
  private getCustomerId!: string;

  private shouldShowPortfolioInvestment = false;
  public shouldShowSuggestions = false;
  public shouldShowRedeemSuggestion = false;
  public showSuggestionModal = false;
  public portabilityRequestsData: PortabilityRequestsData | null = null;

  private newsFeed = [] as IFeedItem[];
  private isLoading = false;

  @Prop({ type: Number, default: 1 })
  public itemPerPage!: number;

  @Prop({ type: Object, required: false })
  private inbox?: GlobalSummaryInbox | null;

  @Prop({ type: Boolean, default: false })
  private showLoader!: boolean;

  @Ref()
  private newsSlidesRefs!: Vue[];

  async created() {
    await this.loadData();
    if (this.$route.query?.from === typeFrom.SuggestionInvestment) {
      this.openInvestmentSuggestionModal();
    }

    if (this.$route.query?.from === typeFrom.SuggestionRedeem) {
      this.openRedeemSuggestionModal();
    }
  }

  public async loadData() {
    try {
      this.isLoading = true;
      await this.loadNewsFeed();

      await this.loadBffNews();
    } finally {
      this.isLoading = false;
    }
  }

  public async loadBffNews() {
    try {
      const { getInvestmentSuggestions, portabilityRequests } = await getNews(
        this.getCustomerId
      );

      this.portabilityRequestsData = portabilityRequests;
      this.shouldShowSuggestions = getInvestmentSuggestions.showSuggestions;
      this.shouldShowPortfolioInvestment =
        getInvestmentSuggestions.showInvestmentSuggestions;

      this.shouldShowRedeemSuggestion =
        !!getInvestmentSuggestions.showRedeemSuggestions;

      if (this.shouldShowRedeemSuggestion) {
        trackRedeemEvents.trackLoadSuggestion();
      }
    } catch {}
  }

  public openInvestmentSuggestionsView() {
    trackerInvestmentEvents.cardCtaAmplitude();
    this.$router.push({ name: 'investmentSuggestions' });
  }

  public openWalletSuggestionModal() {
    SuggestionModule.setSuggestionType(SuggestionType.Wallet);
    this.showSuggestionModal = true;
  }

  public openInvestmentSuggestionModal() {
    SuggestionModule.setSuggestionType(SuggestionType.Investment);
    this.showSuggestionModal = true;
    trackerInvestmentEvents.cardCta();
    trackerInvestmentEvents.cardCtaAmplitude();
  }

  public openRedeemSuggestionModal() {
    SuggestionModule.setSuggestionType(SuggestionType.Redeem);
    this.showSuggestionModal = true;
    trackRedeemEvents.trackViewSuggestion();
  }

  public closeSuggestionModal() {
    this.showSuggestionModal = false;
  }

  private get shouldShowInbox() {
    return (
      this.shouldShowUpdateAccountToRegulation ||
      this.shouldShowCheckingAccount ||
      this.shouldShowPortfolioSuggested ||
      this.showNewsCard ||
      this.shouldShowRedeemSuggestion ||
      this.shouldShowSuggestions ||
      this.portabilityRequestsData
    );
  }

  private get shouldShowCheckingAccount() {
    return (this.inbox?.checkingAccount?.availableBalance ?? 0) > 0;
  }

  private async loadNewsFeed() {
    try {
      const response = await newsApi.getHomeNews();
      this.newsFeed = response.feed || [];
    } catch {}
  }

  @Emit('viewNews')
  public viewNewsFeed(news: IFeedItem): IFeedItem {
    return news;
  }

  @Emit('removeNews')
  public removeNewsFeed(indexFeed: number, feed: IFeedItem) {
    const animationDuration = 500;
    const slideElement = this.newsSlidesRefs[indexFeed].$el as HTMLElement;

    // we need to keep the original element's height which is calculated dinamically by vue-carousel
    slideElement.style.maxHeight = `${slideElement.clientHeight}px`;
    slideElement.classList.add('VueCarousel-slide__news--removed');

    setTimeout(() => {
      this.newsFeed = this.newsFeed.filter((_, index) => index !== indexFeed);
    }, animationDuration);

    return feed;
  }

  private get showNewsCard() {
    return this.newsFeed.length > 0 || this.shouldShowPortfolioInvestment;
  }

  private get hasMultipleCardsPerPage() {
    return this.itemPerPage > 1;
  }

  private get shouldShowPortfolioSuggested() {
    const portfoliosSuggested = this.inbox?.portfoliosSuggested;
    const hasPendingPortfolios =
      portfoliosSuggested &&
      portfoliosSuggested.filter(
        portfolio => portfolio && portfolio.status === 'pending'
      ) &&
      portfoliosSuggested.length > 0;
    return hasPendingPortfolios;
  }

  private get shouldShowUpdateAccountToRegulation(): boolean {
    return !!this.inbox?.updateToAccountRegulation;
  }

  private get hasEmptyMessage() {
    return this.$slots['empty-message'];
  }

  get suggestionType() {
    return SuggestionModule.suggestionType;
  }
}
